const FINCART_BASE_URL = "https://api.fincart.io";
const FINCART_ACCESS_TOKEN =
  "48928b0af412461c1926cedcf062d233:97c323c46a558a85a05ea9c6a00d30bca29dec97b58096ee:25ddbd702c23068dc033e6c6469b3963";

const fincartHeaders = {
  "Content-Type": "application/json",
  Authorization: FINCART_ACCESS_TOKEN,
};

export const normalizeArabicName = (value = "") =>
  value
    .replace(/ى/g, "ي")
    .replace(/\s+/g, " ")
    .trim();

export const fetchFincartGovernorates = async () => {
  const response = await fetch(`${FINCART_BASE_URL}/merchant/config/gov`, {
    method: "GET",
    headers: fincartHeaders,
  });

  if (!response.ok) {
    throw new Error("فشل تحميل المحافظات من Fincart");
  }

  const data = await response.json();
  const governorates = data?.body?.governorates;
  if (!Array.isArray(governorates)) {
    throw new Error("استجابة Fincart غير صالحة (المحافظات)");
  }
  return governorates;
};

export const fetchFincartPickupLocations = async () => {
  const response = await fetch(`${FINCART_BASE_URL}/merchant/locations`, {
    method: "GET",
    headers: fincartHeaders,
  });

  if (!response.ok) {
    throw new Error("فشل تحميل مواقع الاستلام من Fincart");
  }

  const data = await response.json();
  const locations = data?.body;
  if (!Array.isArray(locations) || locations.length === 0) {
    throw new Error("لا يوجد موقع استلام في حساب Fincart");
  }
  return locations;
};

export const getDefaultPickupLocationId = async () => {
  const locations = await fetchFincartPickupLocations();
  const defaultLocation = locations.find((loc) => loc.is_default) || locations[0];
  return defaultLocation._id;
};

/**
 * Map our Arabic state/city to Fincart English city + area names.
 * order.state = governorate (Arabic), order.city = area (Arabic label from checkout).
 */
export const resolveFincartCityArea = (order, governorates) => {
  const stateAr = normalizeArabicName(order.state);
  const areaAr = normalizeArabicName(order.city);

  if (!stateAr) {
    throw new Error("المحافظة غير موجودة");
  }
  if (!areaAr) {
    throw new Error("المدينة/المنطقة غير موجودة — لازم تكون مختارة من قائمة Fincart");
  }

  const gov = governorates.find(
    (g) => normalizeArabicName(g.name_ar) === stateAr
  );
  if (!gov) {
    throw new Error(`المحافظة "${order.state}" غير موجودة في Fincart`);
  }

  const area = (gov.areas || []).find((a) => {
    const candidates = [a.label_ar, a.name_ar, a.label, a.label_en, a.name_en];
    return candidates.some((c) => normalizeArabicName(c) === areaAr);
  });

  if (!area) {
    throw new Error(
      `المنطقة "${order.city}" غير مطابقة لقائمة Fincart في محافظة ${order.state}`
    );
  }

  return {
    city: gov.name,
    area: area.name_en || area.label || area.label_en,
  };
};

const buildDescription = (order) => {
  if (!order.items?.length) return "Girabat order";

  const counts = {};
  order.items.forEach((item) => {
    const key = item.type || item.name || "Item";
    counts[key] = (counts[key] || 0) + 1;
  });

  return Object.entries(counts)
    .map(([name, qty]) => `${qty} X ${name}`)
    .join(", ");
};

/**
 * Build Fincart create-order payload from a Girabatcom enriched order.
 * @see https://fincart.gitbook.io/fincart-api/apis/new-order
 */
export const buildFincartOrderPayload = (order, pickupLocationId, governorates) => {
  const { city, area } = resolveFincartCityArea(order, governorates);
  const phone = String(order.phoneNumber || "").replace(/\D/g, "");
  const backupPhone = String(order.phoneNumber2 || "").replace(/\D/g, "");
  const noOfItems = Math.max(order.items?.length || 1, 1);
  const amount = Number(order.total) || 0;
  const paymentType =
    amount > 0 ? "with_cash_collection" : "without_cash_collection";

  return {
    instaShip: true,
    source: "s2s",
    orderType: "delivery",
    customerDetails: {
      name: order.name,
      phone,
      address: {
        addressLine: order.address || "",
        city,
        area,
        landmark: order.apartment || "",
      },
      ...(backupPhone && backupPhone !== phone ? { backupPhone } : {}),
    },
    orderDetails: {
      paymentType,
      pickupLocationId,
      packageType: "parcel",
      amount,
      noOfItems,
      description: buildDescription(order),
      openPackageAllowed: true,
      subBrand: "Girabatcom",
      referenceNumber: `Girabatcom.${order.orderNumber}`,
      serviceType: "standard",
    },
  };
};

export const createFincartOrder = async (payload) => {
  const response = await fetch(`${FINCART_BASE_URL}/v4/orders/create`, {
    method: "POST",
    headers: fincartHeaders,
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errors = Array.isArray(data?.errors) ? data.errors.join(", ") : "";
    throw new Error(errors || data?.message || `فشل إنشاء الطلب (${response.status})`);
  }

  return data;
};

/**
 * Send one Girabat order to Fincart for shipping.
 * Fincart may still create the order but mark orderSubStatus as "duplicate"
 * when the customer/phone already exists in their system.
 */
export const sendOrderToFincart = async (order, pickupLocationId, governorates) => {
  const payload = buildFincartOrderPayload(order, pickupLocationId, governorates);
  const result = await createFincartOrder(payload);
  const subStatus = String(result?.data?.orderSubStatus || "").toLowerCase();
  return {
    ...result,
    alreadyInFincart: subStatus === "duplicate",
  };
};

const FINCART_ID_MAP_KEY = "fincartOrderIdMap";

export const getFincartOrderIdMap = () => {
  try {
    return JSON.parse(localStorage.getItem(FINCART_ID_MAP_KEY) || "{}");
  } catch {
    return {};
  }
};

export const saveFincartOrderId = (girabatOrderId, fincartOrderId) => {
  if (!girabatOrderId || !fincartOrderId) return;
  const map = getFincartOrderIdMap();
  map[String(girabatOrderId)] = fincartOrderId;
  localStorage.setItem(FINCART_ID_MAP_KEY, JSON.stringify(map));
};

export const getStoredFincartOrderId = (girabatOrderId) =>
  getFincartOrderIdMap()[String(girabatOrderId)] || null;

/**
 * Fetch thermal AWB PDF for one or more Fincart order IDs.
 * @see https://fincart.gitbook.io/fincart-api/apis/print-order-label
 */
export const fetchFincartThermalLabelsPdf = async (fincartOrderIds) => {
  if (!Array.isArray(fincartOrderIds) || fincartOrderIds.length === 0) {
    throw new Error("لا توجد معرفات Fincart للطباعة");
  }

  const response = await fetch(`${FINCART_BASE_URL}/v4/orders/awb`, {
    method: "POST",
    headers: fincartHeaders,
    body: JSON.stringify({
      filters: {
        ids: fincartOrderIds,
        type: "thermal",
      },
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const errors = Array.isArray(data?.errors) ? data.errors.join(", ") : "";
    throw new Error(
      errors || data?.message || `فشل جلب بوليصة الشحن (${response.status})`
    );
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = await response.json();
    throw new Error(data?.message || "استجابة غير متوقعة من Fincart عند الطباعة");
  }

  return response.blob();
};

export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("فشل تحويل ملف البوليصة"));
    reader.readAsDataURL(blob);
  });
